import React, { useState } from 'react';
import { useStore } from '../../store';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  FolderOpen,
  Settings,
  ClipboardList,
} from 'lucide-react';
import DocumentDataPanel from './DocumentDataPanel';
import DocumentPanel from './DocumentPanel';
import SettingsPanel from './SettingsPanel';

type SidebarTab = 'details' | 'documents' | 'settings';

interface SidebarProps {
  onManageTemplates?: () => void;
  onManageDocumentTemplates?: () => void;
}

export default function Sidebar({
  onManageTemplates,
  onManageDocumentTemplates,
}: SidebarProps) {
  const {
    isSidebarOpen,
    isSidebarCollapsed,
    toggleSidebar,
    toggleSidebarCollapse,
    allTemplates,
    currentLetterTemplateId,
    setCurrentLetterTemplate,
    documentTemplates,
    setDocumentTemplates,
    addNotification,
  } = useStore();

  const [activeTab, setActiveTab] = useState<SidebarTab>('details');

  const expandSidebar = () => {
    if (!isSidebarOpen) {
      toggleSidebar();
    }
    if (isSidebarCollapsed) {
      toggleSidebarCollapse();
    }
  };

  const tabs: { id: SidebarTab; label: string; icon: React.ElementType }[] = [
    { id: 'details', label: 'Details', icon: ClipboardList },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Collapsed sidebar - show when sidebar is closed or collapsed
  if (!isSidebarOpen || isSidebarCollapsed) {
    return (
      <div className="fixed right-0 top-16 h-[calc(100vh-4rem)] w-14 bg-white border-l border-gray-200 flex flex-col items-center py-3 gap-1 z-30">
        <button
          onClick={expandSidebar}
          className="p-2 hover:bg-gray-100 rounded-lg mb-2 transition-colors"
          title="Expand sidebar"
        >
          <ChevronLeft size={18} className="text-gray-600" />
        </button>

        <div className="w-8 border-t border-gray-200 mb-2" />

        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                expandSidebar();
              }}
              className={`p-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-accent/10 text-accent'
                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
              }`}
              title={tab.label}
            >
              <Icon size={18} />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="fixed right-0 top-16 h-[calc(100vh-4rem)] w-80 bg-white border-l border-gray-200 shadow-lg flex flex-col z-30 overflow-hidden">
      {/* Header with collapse button */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50/80">
        <div className="flex items-center gap-1.5">
          <FileText size={15} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Document Setup</span>
        </div>
        <button
          onClick={toggleSidebar}
          className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
          title="Collapse sidebar"
        >
          <ChevronRight size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-accent'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon size={14} />
              <span>{tab.label}</span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-accent rounded-t" />
              )}
            </button>
          );
        })}
      </div>

      {/* Page Template Selector */}
      <div className="px-3 py-2 border-b border-gray-100 bg-gray-50/50">
        <label className="block text-xs font-medium text-gray-500 mb-1">Page Template</label>
        <select
          value={currentLetterTemplateId}
          onChange={(e) => setCurrentLetterTemplate(e.target.value)}
          className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent bg-white"
        >
          <option value="">No template</option>
          {allTemplates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name}
            </option>
          ))}
        </select>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
        {activeTab === 'details' && (
          <DocumentDataPanel
            onManagePresets={onManageDocumentTemplates}
            onSaveAsTemplate={(sections) => {
              const name = window.prompt('Enter a name for this preset:');
              if (!name) return;
              const newTemplate = {
                id: `dt_${Date.now()}`,
                name,
                isCustom: true,
                sections,
                createdAt: new Date().toISOString(),
              };
              setDocumentTemplates([...documentTemplates, newTemplate]);
              addNotification(`Preset "${name}" saved`, 'success');
            }}
          />
        )}
        {activeTab === 'documents' && <DocumentPanel />}
        {activeTab === 'settings' && (
          <SettingsPanel
            onManageTemplates={onManageTemplates}
            onManageDocumentTemplates={onManageDocumentTemplates}
          />
        )}
      </div>
    </div>
  );
}
